/**
 * Route guards and the `<Can>` gate (§3). Unauthenticated → /login (with return path); missing permission →
 * /forbidden with an explanation of which permission the page needs. Cross-tenant probes are 404s on the API;
 * the console never needs to distinguish them.
 */
import type { Permission } from '@windrop/contracts';
import { Skeleton } from '@windrop/ui';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useSession } from './session';

export const loginPath = (next?: string): string =>
  next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login';

export const forbiddenPath = (permissions: readonly Permission[], from?: string): string => {
  const params = new URLSearchParams();
  params.set('permission', permissions.join(','));
  if (from) params.set('from', from);
  return `/forbidden?${params.toString()}`;
};

export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();
  if (status === 'loading') {
    return (
      <div className="wd-stack p-6" aria-busy="true">
        <Skeleton shape="text" width="40%" label="Checking your session" />
        <Skeleton shape="block" height={160} />
      </div>
    );
  }
  if (status !== 'authenticated') {
    return <Navigate to={loginPath(`${location.pathname}${location.search}`)} replace />;
  }
  return children ? <>{children}</> : <Outlet />;
}

export interface RequirePermissionProps {
  /** One permission, or several of which ANY satisfies the guard. */
  permission: Permission | readonly Permission[];
  children?: ReactNode;
}

const toList = (p: Permission | readonly Permission[]): readonly Permission[] =>
  typeof p === 'string' ? [p] : p;

export const hasAny = (can: (p: Permission) => boolean, p: Permission | readonly Permission[]): boolean =>
  toList(p).some((x) => can(x));

export function RequirePermission({ permission, children }: RequirePermissionProps) {
  const { can, status } = useSession();
  const location = useLocation();
  if (status === 'loading') return null;
  if (status !== 'authenticated') return <Navigate to={loginPath(location.pathname)} replace />;
  if (!hasAny(can, permission)) {
    return <Navigate to={forbiddenPath(toList(permission), location.pathname)} replace />;
  }
  return children ? <>{children}</> : <Outlet />;
}

export interface CanProps {
  permission: Permission | readonly Permission[];
  /** Rendered when the permission is missing (default: nothing). */
  fallback?: ReactNode;
  children: ReactNode;
}

/** Shows children only when the session holds the permission — use for buttons, menu items, table actions. */
export function Can({ permission, fallback = null, children }: CanProps) {
  const { can } = useSession();
  return hasAny(can, permission) ? <>{children}</> : <>{fallback}</>;
}
